import { formatStatNumber } from "utils/percentage";
import { formatMoney } from "utils/money";

const PercentageSummaryCard = ({
    title,
    contextName,
    savedPercentage,
    currentValue,
    onChange,
    onReset,
    onPersist,
    isSaving,
    canPersist,
    saveLabel,
    emptySelectionHint,
    basisLabel,
    basisValue,
    resultValue,
    isDirty,
    inputId,
    hintText,
    // What "رجوع" restores when nothing is saved: the rate the server resolved
    // for this driver or restaurant. Passed in rather than assumed, because
    // 5% and 15% are different answers and this card serves both.
    baselineValue = null,
    // The rate that applies when nothing at all is picked — the city's default,
    // or the contract value. From GET /api/commission-defaults, never from a
    // number written here. See services/commissionDefaults.js.
    fallbackValue = null,
}) => {
    const hasSavedValue = savedPercentage !== null && typeof savedPercentage !== "undefined";
    const hasBaseline = baselineValue !== null && typeof baselineValue !== "undefined";
    const hasCurrentValue =
        currentValue !== "" && currentValue !== null && typeof currentValue !== "undefined";

    // NEVER «--». The card used to print «-- × 44,319.61» before a restaurant
    // was picked, which reads as a broken figure rather than an unanswered one.
    // It now names the rate that WOULD apply, and marks it as the fallback, so
    // the equation stays readable and stays honest at the same time.
    const shownPercentage = hasCurrentValue
        ? Number(currentValue)
        : hasBaseline
          ? baselineValue
          : fallbackValue;
    const isFallbackShown =
        !hasCurrentValue && shownPercentage !== null && typeof shownPercentage !== "undefined";
    const displayPercentage =
        shownPercentage === null || typeof shownPercentage === "undefined"
            ? "--"
            : `${formatStatNumber(shownPercentage)}%`;
    // The result follows the rate on screen. A fallback rate beside a 0 result
    // would be two different answers to the same question.
    const displayResult = hasCurrentValue
        ? resultValue
        : isFallbackShown
          ? (Number(basisValue || 0) * Number(shownPercentage)) / 100
          : 0;
    const headline = contextName || "قيمة قابلة للتعديل";
    const supportingText = contextName ? `تُحسب مباشرة من ${basisLabel}` : emptySelectionHint;

    return (
        <div className="stat-box stat-box--percentage">
            <div className="percentage-card__header">
                <div className="percentage-card__copy">
                    <div className="percentage-card__eyebrow">{title}</div>
                    <h4>{headline}</h4>
                    <div className="percentage-card__subcopy">{supportingText}</div>
                </div>

                <div className={`percentage-card__saved ${hasSavedValue ? "" : "is-empty"}`}>
                    <span>القيمة المحفوظة</span>
                    <strong>
                        {hasSavedValue ? `${formatStatNumber(savedPercentage)}%` : "غير محفوظة"}
                    </strong>
                </div>
            </div>

            <div className="percentage-card__workspace">
                <div className="percentage-card__panel percentage-card__editor">
                    <label className="percentage-card__field-label" htmlFor={inputId}>
                        النسبة الحالية
                    </label>
                    <div className={`percentage-card__input-wrap ${isDirty ? "is-dirty" : ""}`}>
                        <input
                            id={inputId}
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={currentValue}
                            onChange={onChange}
                            className="percent-input"
                        />
                        <span className="percentage-card__suffix">%</span>
                    </div>
                    <div className={`percentage-card__state ${isDirty ? "is-dirty" : ""}`}>
                        {isDirty
                            ? "تعديل مؤقت غير محفوظ"
                            : hasSavedValue
                              ? "مطابقة للقيمة المحفوظة"
                              : "يمكنك استخدامها مؤقتاً أو حفظها لاحقاً"}
                    </div>
                </div>

                <div className="percentage-card__panel percentage-card__formula">
                    <span className="percentage-card__panel-label">المعادلة الحالية</span>
                    <div className="percentage-card__formula-line">
                        <strong>{displayPercentage}</strong>
                        {isFallbackShown ? (
                            <span className="percentage-card__default-badge">افتراضي</span>
                        ) : null}
                        <span className="percentage-card__formula-mark">×</span>
                        <strong>{formatMoney(basisValue)}</strong>
                    </div>
                    <div className="percentage-card__formula-hint">{basisLabel}</div>
                </div>

                <div className="percentage-card__panel percentage-card__result">
                    <span className="percentage-card__panel-label">الناتج الحالي</span>
                    <strong className="percentage-card__result-value">
                        {formatMoney(displayResult)}
                    </strong>
                    <div className="percentage-card__result-note">
                        {isFallbackShown
                            ? "محسوب بالنسبة الافتراضية"
                            : "ناتج التطبيق المباشر على العرض"}
                    </div>
                </div>
            </div>

            <div className="percentage-card__footer">
                <div className="percentage-card__hint">{hintText}</div>
                <div className="percentage-card__actions">
                    <button type="button" className="percentage-card__ghost" onClick={onReset}>
                        {/* The fallback used to be the literal "لـ 5%", which was
                            wrong on the drivers page — their rate is 15% — and
                            wrong for any city on its own rate. It names the
                            baseline it will actually restore, and says nothing
                            about a number when there is none to restore. */}
                        رجوع{" "}
                        {hasSavedValue
                            ? "للمحفوظ"
                            : hasBaseline
                              ? `لـ ${formatStatNumber(baselineValue)}%`
                              : fallbackValue !== null && typeof fallbackValue !== "undefined"
                                ? `لـ ${formatStatNumber(fallbackValue)}%`
                                : "للافتراضي"}
                    </button>
                    <button
                        type="button"
                        className="percentage-card__primary"
                        onClick={onPersist}
                        disabled={!canPersist || isSaving}
                    >
                        {isSaving ? "جاري الحفظ..." : saveLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PercentageSummaryCard;
